import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress,  Alert,
  Paper,
  useTheme,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,  Collapse,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  TrendingUp,
  Error,
  Cancel,
  CheckCircle,
  Refresh,
  Store,
  Assignment,
  ExpandMore,
  ExpandLess,
  Sync,
  Send,
  Info
} from '@mui/icons-material';
import { OrderCountersOnly, DashboardSettings, BranchSummary, OrderSummary, OrderStatus } from '../types';
import { apiService } from '../services/apiService';
import { usePermission } from '../hooks/usePermissions';

// LocalStorage keys
const DASHBOARD_SETTINGS_KEY = 'dashboard_settings';
const ORDERS_AUTO_REFRESH_KEY = 'orders_auto_refresh';

interface CounterCardProps {
  title: string;
  count: number;
  sales?: number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
}

const CounterCard: React.FC<CounterCardProps> = ({ title, count, sales, subtitle, icon, color, loading }) => {
  const theme = useTheme();
  
  return (
    <Card sx={{ height: '100%', position: 'relative', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', p: 2 }}>
        {/* Header with title and icon */}
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
          <Box sx={{ flex: 1, mr: 1 }}>
            <Typography color="textSecondary" gutterBottom variant="h6" sx={{ wordBreak: 'break-word', fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' } }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: alpha(theme.palette[color as 'primary' | 'error' | 'warning' | 'success' | 'info'].main, 0.1),
              borderRadius: '50%',
              width: { xs: 48, sm: 56, md: 64 },
              height: { xs: 48, sm: 56, md: 64 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <Box sx={{ 
              color: theme.palette[color as 'primary' | 'error' | 'warning' | 'success' | 'info'].main, 
              fontSize: { xs: 24, sm: 28, md: 32 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {icon}
            </Box>
          </Box>
        </Box>        {/* Counter only at bottom - sales hidden for status cards */}
        <Box sx={{ mt: 'auto' }}>
          <Typography variant="h4" component="h2" color={color} fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
            {loading ? <CircularProgress size={24} color="inherit" /> : count}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const DashboardPage: React.FC = () => {
  const theme = useTheme();
  
  // Permission checks
  const canViewOrders = usePermission('orders:read');
  const canViewBranches = usePermission('branches:read');
  // const canViewReports = usePermission('reports:read'); // Unused for now
  // const canManageSystem = usePermission('system:superadmin_manage'); // Unused for now
  const [counters, setCounters] = useState<OrderCountersOnly>({
    new: 0,
    sentToPOS: 0,
    failed: 0,
    cancelled: 0,
    closed: 0
  });
  const [branchSummary, setBranchSummary] = useState<BranchSummary>({
    total: 0,
    active: 0,
    syncEnabled: 0,
    menuSynced: 0
  });
  const [latestOrders, setLatestOrders] = useState<OrderSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [branchesLoading, setBranchesLoading] = useState<boolean>(true);
  const [ordersLoading, setOrdersLoading] = useState<boolean>(true);
  const [isReloading, setIsReloading] = useState<boolean>(false); // Master reload state
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [showSettings, setShowSettings] = useState<boolean>(false);

  // Dashboard settings
  const [settings, setSettings] = useState<DashboardSettings>(() => {
    const saved = localStorage.getItem(DASHBOARD_SETTINGS_KEY);
    const defaultSettings = {
      intervalDays: 1,
      autoRefresh: false,
      refreshInterval: 30
    };
    
    if (saved) {
      try {
        const parsedSettings = JSON.parse(saved);
        // Ensure all required properties exist with defaults
        return {
          intervalDays: parsedSettings.intervalDays ?? defaultSettings.intervalDays,
          autoRefresh: parsedSettings.autoRefresh ?? defaultSettings.autoRefresh,
          refreshInterval: parsedSettings.refreshInterval ?? defaultSettings.refreshInterval
        };
      } catch (error) {
        console.warn('Failed to parse dashboard settings from localStorage:', error);
        return defaultSettings;
      }
    }
    
    return defaultSettings;
  });
  // Auto-refresh timer
  const refreshTimer = useRef<NodeJS.Timeout | null>(null);

  // Save settings to localStorage
  const saveSettings = useCallback((newSettings: DashboardSettings) => {
    localStorage.setItem(DASHBOARD_SETTINGS_KEY, JSON.stringify(newSettings));
    setSettings(newSettings);
  }, []);

  // Save auto-refresh settings for orders page
  const saveOrdersAutoRefresh = useCallback((autoRefresh: boolean, refreshInterval: number) => {
    localStorage.setItem(ORDERS_AUTO_REFRESH_KEY, JSON.stringify({ autoRefresh, refreshInterval }));
  }, []);  // Fetch order counters - only if user has permission
  const fetchCounters = useCallback(async () => {
    if (!canViewOrders || isReloading) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getOrderCountersOnly(
        settings.intervalDays || 1
      );
      setCounters(data as unknown as OrderCountersOnly);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching order counters:', err);
      setError('Failed to fetch order counters');
    } finally {
      setLoading(false);
    }
  }, [settings.intervalDays, canViewOrders, isReloading]);

  // Fetch branches summary - only if user has permission
  const fetchBranchesSummary = useCallback(async () => {
    if (!canViewBranches || isReloading) return;
    
    try {
      setBranchesLoading(true);
      const data = await apiService.getBranchesSummary();
      setBranchSummary(data);
    } catch (err) {
      console.error('Error fetching branches summary:', err);
    } finally {
      setBranchesLoading(false);
    }
  }, [canViewBranches, isReloading]);

  // Fetch latest orders - only if user has permission
  const fetchLatestOrders = useCallback(async () => {
    if (!canViewOrders || isReloading) return;
    
    try {
      setOrdersLoading(true);
      const data = await apiService.getLatestOrders(5);
      setLatestOrders(data);
    } catch (err) {
      console.error('Error fetching latest orders:', err);
    } finally {
      setOrdersLoading(false);
    }
  }, [canViewOrders, isReloading]);

  // Fetch all data with reload protection
  const fetchAllData = useCallback(async () => {
    if (isReloading) {
      console.log('Reload already in progress, skipping...');
      return;
    }

    try {
      setIsReloading(true);
      setError(null);
      
      await Promise.all([
        fetchCounters(),
        fetchBranchesSummary(),
        fetchLatestOrders()
      ]);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to fetch dashboard data');
    } finally {
      setIsReloading(false);
    }
  }, [fetchCounters, fetchBranchesSummary, fetchLatestOrders, isReloading]);  // Set up auto-refresh with reload protection
  useEffect(() => {
    // Clear existing timer
    if (refreshTimer.current) {
      clearInterval(refreshTimer.current);
      refreshTimer.current = null;
    }

    // Set up new timer if auto-refresh is enabled
    if (settings.autoRefresh && settings.refreshInterval > 0) {
      refreshTimer.current = setInterval(() => {
        // Only auto-refresh if no manual reload is in progress
        if (!isReloading) {
          fetchAllData();
        } else {
          console.log('Auto-refresh skipped: manual reload in progress');
        }
      }, settings.refreshInterval * 1000);
    }

    // Cleanup function
    return () => {
      if (refreshTimer.current) {
        clearInterval(refreshTimer.current);
        refreshTimer.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.autoRefresh, settings.refreshInterval, isReloading]); // Include isReloading to respect reload state  // Fetch data on initial load and when filter settings change
  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.intervalDays]); // Only depend on filter settings, not fetch functions
  const handleIntervalChange = (intervalDays: number) => {
    const newSettings = { ...settings, intervalDays };
    saveSettings(newSettings);
  };

  const handleAutoRefreshToggle = (autoRefresh: boolean) => {
    const newSettings = { ...settings, autoRefresh };
    saveSettings(newSettings);
    saveOrdersAutoRefresh(autoRefresh, settings.refreshInterval);
  };
  const handleRefreshIntervalChange = (refreshInterval: number) => {
    const newSettings = { ...settings, refreshInterval };
    saveSettings(newSettings);
    saveOrdersAutoRefresh(settings.autoRefresh, refreshInterval);
  };

  // Day start hour functionality removed

  const getIntervalText = (days: number) => {
    if (days === -1) return 'Yesterday';
    if (days === 1) return 'Today';
    if (days === 2) return 'Last 2 days';    if (days === 7) return 'Last 7 days';
    return `Last ${days} days`;
  };

  const getStatusColor = (status: string | number | undefined) => {
    if (status === undefined || status === null) {
      return 'default';
    }
    
    const statusStr = typeof status === 'number' ? 
      Object.keys(OrderStatus)[Object.values(OrderStatus).indexOf(status)] || status.toString() :
      status;
    
    switch (statusStr.toLowerCase()) {
      // Local POS statuses
      case 'new':
      case 'pending':
        return 'primary';
      case 'failed':
      case 'cancelfailed':
      case 'closefailed':
        return 'error';
      case 'cancelledinpos':
        return 'warning';
      case 'closedinpos':
        return 'success';
      case 'senttopos':
        return 'info';
        // Cloud platform statuses
      case 'accepted':
      case 'preorder_accepted':
        return 'primary';
      case 'reconciled_with_platform':
      case 'reconciled_with_transport':
        return 'success';
      case 'cancelled':
      case 'cancelled_by_platform':
      case 'cancelled_by_transport':
        return 'error';
      case 'waiting_for_transport':
      case 'modified_by_vendor':
      case 'reconciliation_started':
        return 'warning';
      case 'closed':
        return 'success';
      
      default:
        return 'default';
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };
  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* Compact Settings Panel */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, cursor: 'pointer' }} onClick={() => setShowSettings(!showSettings)}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              Settings
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <Box display="flex" gap={1}>                <Chip 
                  label={getIntervalText(settings.intervalDays)} 
                  color="primary" 
                  size="small"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent event bubbling to parent
                    // Toggle through interval options
                    const intervals = [-1, 1, 2, 7];
                    const currentIndex = intervals.indexOf(settings.intervalDays);
                    const nextIndex = (currentIndex + 1) % intervals.length;
                    handleIntervalChange(intervals[nextIndex]);
                  }}
                />{settings.autoRefresh && (
                  <Chip 
                    label={`Auto: ${settings.refreshInterval}s`} 
                    color="success" 
                    size="small"
                    variant="outlined"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent event bubbling to parent
                      // Show refresh interval options or toggle through them
                      const intervals = [15, 30, 60, 300];
                      const currentIndex = intervals.indexOf(settings.refreshInterval);
                      const nextIndex = (currentIndex + 1) % intervals.length;
                      handleRefreshIntervalChange(intervals[nextIndex]);
                    }}
                  />
                )}
              </Box>
              <Tooltip title={isReloading ? "Reload in progress..." : "Refresh dashboard"}>
                <IconButton 
                  size="small" 
                  onClick={(e) => { e.stopPropagation(); fetchAllData(); }}
                  disabled={isReloading}
                  sx={{ 
                    color: isReloading ? 'text.disabled' : 'inherit',
                    '&.Mui-disabled': {
                      color: 'text.disabled'
                    }
                  }}
                >
                  {isReloading ? <CircularProgress size={16} /> : <Refresh />}
                </IconButton>
              </Tooltip>
              <IconButton size="small" onClick={() => setShowSettings(!showSettings)}>
                {showSettings ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
          </Box>
        </Box>
        <Collapse in={showSettings}>
          <Box sx={{ p: 3, pt: 0 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
              <Box sx={{ minWidth: 200, flex: '1 1 300px' }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Time Interval</InputLabel>
                  <Select
                    value={settings.intervalDays}
                    label="Time Interval"
                    onChange={(e) => handleIntervalChange(Number(e.target.value))}
                  >                    <MenuItem value={-1}>Yesterday</MenuItem>
                    <MenuItem value={1}>Today</MenuItem>
                    <MenuItem value={2}>Last 2 days</MenuItem>
                    <MenuItem value={7}>Last 7 days</MenuItem>
                  </Select>                </FormControl>
              </Box>
              {/* Day Start Hour selection removed */}
              <Box sx={{ minWidth: 150, flex: '1 1 200px' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.autoRefresh}
                      onChange={(e) => handleAutoRefreshToggle(e.target.checked)}
                    />
                  }
                  label="Auto Refresh"
                />
              </Box>
              {settings.autoRefresh && (
                <Box sx={{ minWidth: 200, flex: '1 1 300px' }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Refresh Interval</InputLabel>
                    <Select
                      value={settings.refreshInterval}
                      label="Refresh Interval"
                      onChange={(e) => handleRefreshIntervalChange(Number(e.target.value))}
                    >
                      <MenuItem value={15}>15 seconds</MenuItem>
                      <MenuItem value={30}>30 seconds</MenuItem>
                      <MenuItem value={60}>1 minute</MenuItem>
                      <MenuItem value={300}>5 minutes</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              )}              <Box sx={{ minWidth: 200, flex: '1 1 300px' }}>
                {lastUpdated && (
                  <Typography variant="caption" color="textSecondary">                    Last updated: {lastUpdated.toLocaleTimeString()}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Collapse>
      </Paper>{/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}      {/* Main Dashboard Grid */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {/* Order Counter Cards */}
        <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <CounterCard
            title="New Orders"
            count={counters.new}
            icon={<TrendingUp />}
            color="primary"
            loading={loading}
          />
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <CounterCard
            title="Sent to POS"
            count={counters.sentToPOS}
            icon={<Send />}
            color="info"
            loading={loading}
          />
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <CounterCard
            title="Failed Orders"
            count={counters.failed}
            icon={<Error />}
            color="error"
            loading={loading}
          />
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <CounterCard
            title="Cancelled Orders"
            count={counters.cancelled}
            icon={<Cancel />}
            color="warning"
            loading={loading}
          />
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <CounterCard
            title="Closed Orders"
            count={counters.closed}
            icon={<CheckCircle />}
            color="success"
            loading={loading}
          />
        </Box>
      </Box>

      {/* Dashboard Content */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 3 }}>
        {/* Branches Summary */}
        <Box sx={{ flex: '1 1 400px' }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <Store /> Branches Summary
              </Typography>
              {branchesLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: '1 1 120px', textAlign: 'center' }}>
                    <Typography variant="h3" color="primary" fontWeight="bold">
                      {branchSummary.total}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total Branches
                    </Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 120px', textAlign: 'center' }}>
                    <Typography variant="h3" color="success.main" fontWeight="bold">
                      {branchSummary.active}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Active
                    </Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 120px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Sync color="primary" />
                      <Typography variant="h3" color="primary" fontWeight="bold">
                        {branchSummary.syncEnabled}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      Enabled
                    </Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 120px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Assignment color="success" />
                      <Typography variant="h3" color="success.main" fontWeight="bold">
                        {branchSummary.menuSynced}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      Order Sync Enabled
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Latest Orders */}
        <Box sx={{ flex: '2 1 600px' }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <Assignment /> Latest Orders
              </Typography>
              {ordersLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                  <CircularProgress />
                </Box>
              ) : latestOrders.length === 0 ? (
                <Typography color="textSecondary" textAlign="center" mt={2}>
                  No recent orders to display.
                </Typography>              ) : (                <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 'none' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Order ID</TableCell>
                        <TableCell>Customer</TableCell>
                        <TableCell>Branch</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Cloud State</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {latestOrders.slice(0, 8).map((order) => (
                        <TableRow key={order.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {order.cloudShortCode || order.cloudOrderId || order.id.substring(0, 8)}
                            </Typography>                            <Typography variant="caption" color="textSecondary">
                              {formatDateTime(order.orderTime)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {order.customerName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {order.branchName || 'Unknown'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold">
                              ${order.totalAmount?.toFixed(2)}
                            </Typography>
                          </TableCell>                          <TableCell>
                            <Chip
                              label={order.cloudOrderState || order.status || 'Unknown'}
                              color={getStatusColor(order.cloudOrderState || order.status)}
                              size="small"
                              variant="filled"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      {latestOrders.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography variant="body2" color="textSecondary">
                              No recent orders found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Additional Information */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          About Dashboard
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          This dashboard provides a comprehensive overview of your restaurant operations including order statistics, 
          branch information, and recent activity. Auto-refresh settings are synchronized with the Orders page.
        </Typography>        <Typography variant="body2" color="textSecondary">
          • <strong>Order Counters:</strong> Based on selected time interval. "Today" shows orders for the current calendar day (midnight to midnight)<br/>
          • <strong>Revenue Summary:</strong> Restaurant revenue (items only) vs Talabat total sales (with delivery fees and platform discounts)<br/>
          • <strong>Talabat Discount Impact:</strong> Analysis of platform charges including delivery fees and Talabat discounts applied to orders<br/>
          • <strong>Payment Summary:</strong> Orders and revenue split by payment method (Prepaid vs Cash on Delivery)<br/>
          • <strong>Branches Summary:</strong> Real-time status of all restaurant branches and sync status<br/>
          • <strong>Latest Orders:</strong> Most recent orders from the last 24 hours with customer, branch, and status information<br/>
          • <strong>Platform Charges:</strong> Includes delivery fees, Talabat discounts, and other platform-applied charges that affect final revenue
        </Typography>
      </Paper>
    </Box>
  );
};

export default DashboardPage;
